import type { CountryCode } from 'libphonenumber-js';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { flushSync } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { AuthLayout } from '../components/ui/AuthLayout';
import { Button } from '../components/ui/Button';
import { CountrySelect } from '../components/ui/CountrySelect';
import { Field } from '../components/ui/Field';
import { AlertCircle, ArrowBack, Mail, Phone } from '../components/ui/icons';
import { LiveMark } from '../components/ui/LiveMark';
import { OtpInput, type OtpInputHandle } from '../components/ui/OtpInput';
import { SegmentedControl } from '../components/ui/SegmentedControl';
import {
  api,
  ApiError,
  clearInviteCode,
  prefetchHistory,
  readRememberedCountry,
  rememberCountry,
  takeInviteCode,
} from '../lib/api';
import { detectCountry, formatAsYouType, isKnownCountry, phoneStatus } from '../lib/countries';
import { maskIdentifier } from '../lib/mask';
import { useSession } from '../lib/session';
import { DELETED_FLAG } from './Privacy';
import { formatSeconds, useCountdown } from '../lib/useCountdown';

type Method = 'phone' | 'email';
type Step = 'identifier' | 'code';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function SignInPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { me, loading, setMe } = useSession();

  const [method, setMethod] = useState<Method>('phone');
  // M5.5: «حُذفت بياناتك.» once, after the person deleted everything.
  const [deletedNotice] = useState<boolean>(() => {
    try {
      const v = sessionStorage.getItem(DELETED_FLAG);
      if (v) sessionStorage.removeItem(DELETED_FLAG);
      return Boolean(v);
    } catch {
      return false;
    }
  });
  // Last chosen country (this browser) first, then the browser guess (locale region, time zone);
  // the server hint may override the guess below.
  const remembered = readRememberedCountry();
  const [detected, setDetected] = useState<CountryCode | undefined>(() => detectCountry());
  const [country, setCountryState] = useState<CountryCode>(
    () => (isKnownCountry(remembered) ? remembered : undefined) ?? detectCountry() ?? 'QA',
  );
  const setCountry = (c: CountryCode) => {
    setCountryState(c);
    rememberCountry(c);
  };
  const [phone, setPhone] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  const [step, setStep] = useState<Step>('identifier');
  const [destination, setDestination] = useState('');
  const [code, setCode] = useState('');
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [busy, setBusy] = useState<'send' | 'verify' | 'resend' | null>(null);
  const [error, setError] = useState<{ key: string; until?: number } | null>(null);
  const [debugCode, setDebugCode] = useState<string | undefined>();
  const [resendUntil, setResendUntil] = useState<number | null>(null);
  /** Carrier said the text did not go through (from the status callback / Attempts API). */
  const [smsFailed, setSmsFailed] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const inviteCode = takeInviteCode();
  const phoneRef = useRef<HTMLInputElement>(null);
  const otpRef = useRef<OtpInputHandle>(null);

  const resendLeft = useCountdown(resendUntil);
  const blockedLeft = useCountdown(blockedUntil);
  const errorLeft = useCountdown(error?.until ?? null);

  // Country: server hint (cf.country in production) is the detected country; it wins over the
  // browser guess, but never over a country the person chose before.
  useEffect(() => {
    let cancelled = false;
    api
      .locale()
      .then((hint) => {
        if (cancelled || !isKnownCountry(hint.country)) return;
        if (hint.source === 'cf') setDetected(hint.country);
        if (
          !isKnownCountry(remembered) &&
          (hint.source === 'cf' || detectCountry() === undefined)
        ) {
          setCountryState(hint.country);
        }
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loading && me) void navigate(me.onboarded ? '/two' : '/onboarding', { replace: true });
  }, [loading, me, navigate]);

  const phoneState = useMemo(() => phoneStatus(phone, country), [phone, country]);
  const phoneError =
    phoneTouched && phone && !phoneState.valid
      ? phoneState.tooShort
        ? t('signin.phoneTooShort')
        : t('signin.phoneInvalid')
      : undefined;
  const emailValid = EMAIL_RE.test(email.trim());
  const emailError = emailTouched && email && !emailValid ? t('signin.emailInvalid') : undefined;
  const canSend =
    !blockedLeft && (method === 'phone' ? phoneState.valid : emailValid) && busy === null;

  const identifier = method === 'phone' ? phone : email.trim();
  /** What the code screen shows while the request is in flight (E.164 when known). */
  const provisionalDestination = method === 'phone' ? (phoneState.e164 ?? phone) : identifier;

  const applyError = useCallback((err: unknown) => {
    if (err instanceof ApiError) {
      const retry = err.retryAfterSeconds;
      if (err.code === 'resend_cooldown' && retry) {
        setResendUntil(Date.now() + retry * 1000);
        setError({ key: err.code, until: Date.now() + retry * 1000 });
        return;
      }
      if ((err.code === 'rate_limited_identifier' || err.code === 'rate_limited_ip') && retry) {
        const until = Date.now() + retry * 1000;
        setBlockedUntil(until);
        setError({ key: err.code, until });
        return;
      }
      setError({ key: err.code });
      return;
    }
    setError({ key: 'network' });
  }, []);

  /**
   * After an SMS is accepted by Twilio, the carrier may still reject it silently. Poll the
   * delivery status for half a minute and, on a failure, say so in one line and offer email.
   */
  const deliveryWatch = useRef(0);
  const watchDelivery = useCallback((identifier: string, forCountry: string) => {
    const token = ++deliveryWatch.current;
    const started = Date.now();
    const tick = async () => {
      if (deliveryWatch.current !== token || Date.now() - started > 30_000) return;
      try {
        const s = await api.otpStatus(identifier, forCountry);
        if (deliveryWatch.current !== token) return;
        if (s.status === 'failed') {
          setSmsFailed(true);
          return;
        }
        if (s.status === 'delivered') return;
      } catch {
        // keep polling
      }
      setTimeout(() => void tick(), 3000);
    };
    setTimeout(() => void tick(), 3000);
  }, []);
  useEffect(() => {
    const ref = deliveryWatch;
    return () => {
      ref.current += 1;
    };
  }, []);

  /**
   * Latency perception: after client-side validation the code screen appears immediately with
   * the field disabled and "Sending…"; a failure returns to the identifier step with the error.
   * The OTP field is focused synchronously inside the tap (flushSync commits the step first)
   * so the numeric keyboard stays open.
   */
  const send = async (kind: 'send' | 'resend') => {
    setError(null);
    if (kind === 'send') {
      flushSync(() => {
        setBusy(kind);
        setDestination(provisionalDestination);
        setCode('');
        setCodeInvalid(false);
        setDebugCode(undefined);
        setStep('code');
      });
      otpRef.current?.focus();
    } else {
      setBusy(kind);
    }
    try {
      const res = await api.requestOtp(
        identifier,
        method === 'phone' ? country : undefined,
        i18n.resolvedLanguage,
      );
      setDestination(res.identifier);
      setDebugCode(res.debug_code);
      setResendUntil(Date.now() + res.resend_cooldown_seconds * 1000);
      setSmsFailed(false);
      if (kind === 'resend') {
        setCode('');
        setCodeInvalid(false);
      }
      if (res.delivery === 'sms') watchDelivery(res.identifier, country);
    } catch (err) {
      if (kind === 'send') setStep('identifier');
      applyError(err);
    } finally {
      setBusy(null);
    }
  };

  /** "Use email instead": back to the identifier step on the email method. */
  const useEmail = () => {
    deliveryWatch.current += 1;
    setMethod('email');
    setStep('identifier');
    setCode('');
    setCodeInvalid(false);
    setSmsFailed(false);
    setError(null);
  };

  const onSubmitIdentifier = (e: FormEvent) => {
    e.preventDefault();
    setPhoneTouched(true);
    setEmailTouched(true);
    if (!canSend) return;
    void send('send');
  };

  const verify = useCallback(
    async (fullCode: string) => {
      setBusy('verify');
      setError(null);
      try {
        const res = await api.verifyOtp(destination, fullCode, {
          country: method === 'phone' ? country : undefined,
          invite_code: inviteCode,
        });
        clearInviteCode();
        // The shell mounts next: history loads in parallel with the socket connect.
        prefetchHistory();
        setMe(res.me);
        const from = (location.state as { from?: string } | null)?.from;
        void navigate(res.me.onboarded ? (from ?? '/two') : '/onboarding', { replace: true });
      } catch (err) {
        setCodeInvalid(true);
        setCode('');
        applyError(err);
        setBusy(null);
      }
    },
    [destination, method, country, inviteCode, setMe, location.state, navigate, applyError],
  );

  const changeIdentifier = () => {
    deliveryWatch.current += 1;
    setSmsFailed(false);
    setStep('identifier');
    setCode('');
    setCodeInvalid(false);
    setError(null);
    setTimeout(() => phoneRef.current?.focus({ preventScroll: true }), 0);
  };

  const errorMessage = error
    ? t(`signin.errors.${error.key}`, {
        defaultValue: t('common.error'),
        time: formatSeconds(errorLeft),
      })
    : null;

  const consent = (
    <p className="text-xs leading-relaxed text-ink-3">
      <Trans
        i18nKey="signin.consent"
        components={{
          terms: (
            <a
              href="#terms"
              className="underline decoration-line-strong underline-offset-2 hover:text-ink"
            />
          ),
          privacy: (
            <a
              href="#privacy"
              className="underline decoration-line-strong underline-offset-2 hover:text-ink"
            />
          ),
        }}
      />
    </p>
  );

  return (
    <AuthLayout>
      {deletedNotice ? (
        <p
          role="status"
          className="mb-4 rounded-md bg-success-soft px-3 py-2 text-sm text-success-text"
          data-deleted-notice
        >
          {t('privacy.deleted')}
        </p>
      ) : null}
      {step === 'identifier' ? (
        <form
          onSubmit={onSubmitIdentifier}
          noValidate
          className="animate-rise flex flex-1 flex-col gap-6"
        >
          <div className="pt-4 lg:pt-0">
            <h1 className="text-2xl font-semibold tracking-tight text-balance">
              {t('signin.title')}
            </h1>
            <p className="mt-2 text-base text-ink-2">{t('signin.subtitle')}</p>
            {inviteCode ? (
              <p className="mt-3 rounded-md bg-brand-soft px-3 py-2 text-sm text-ink">
                {t('signin.inviteDetected')}
              </p>
            ) : null}
          </div>

          <SegmentedControl<Method>
            label={t('signin.method')}
            value={method}
            onChange={(m) => {
              setMethod(m);
              setError(null);
            }}
            options={[
              { value: 'phone', label: t('signin.methodPhone'), icon: <Phone size={18} /> },
              { value: 'email', label: t('signin.methodEmail'), icon: <Mail size={18} /> },
            ]}
          />

          {method === 'phone' ? (
            <div className="flex flex-col gap-1.5">
              <label htmlFor="phone" className="text-sm font-medium text-ink">
                {t('signin.phone')}
              </label>
              <div className="flex gap-2" dir="ltr">
                <CountrySelect
                  value={country}
                  onChange={setCountry}
                  disabled={busy !== null}
                  detected={detected}
                />
                <Field
                  ref={phoneRef}
                  id="phone"
                  label={t('signin.phone')}
                  hideLabel
                  ltr
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel-national"
                  enterKeyHint="send"
                  placeholder={t('signin.phonePlaceholder')}
                  value={phone}
                  onChange={(e) => setPhone(formatAsYouType(e.target.value, country))}
                  onBlur={() => setPhoneTouched(true)}
                  disabled={busy !== null}
                  className="tabular"
                  aria-describedby="phone-help"
                />
              </div>
              <p
                id="phone-help"
                role={phoneError ? 'alert' : undefined}
                className={[
                  'flex min-h-10 items-start gap-1.5 text-sm',
                  phoneError ? 'text-critical-text' : 'text-ink-2',
                ].join(' ')}
              >
                {phoneError ? <AlertCircle size={16} className="mt-0.5 shrink-0" /> : null}
                <span>{phoneError ?? t('signin.phoneHint')}</span>
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Field
                id="email"
                label={t('signin.email')}
                ltr
                type="email"
                inputMode="email"
                autoComplete="email"
                enterKeyHint="send"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                error={emailError}
                disabled={busy !== null}
                leading={<Mail size={18} className="text-ink-3" />}
              />
              {/* The helper line's height is reserved on both methods, so the switch moves nothing (M5.2). */}
              {emailError ? null : (
                <p className="flex min-h-10 items-start text-sm text-ink-2" aria-hidden>
                  {t('signin.emailHint')}
                </p>
              )}
            </div>
          )}

          <div className="mt-auto flex flex-col gap-3 lg:mt-2">
            {errorMessage ? (
              <p
                role="alert"
                aria-live="assertive"
                className="flex items-start gap-2 rounded-md bg-critical-soft px-3 py-2 text-sm text-critical-text"
              >
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <span>
                  {errorMessage}
                  {method === 'phone' && error?.key === 'delivery_failed' ? (
                    <>
                      {' '}
                      <button type="button" onClick={useEmail} className="font-medium underline">
                        {t('signin.useEmail')}
                      </button>
                    </>
                  ) : null}
                </span>
              </p>
            ) : null}
            <Button type="submit" size="lg" block disabled={!canSend}>
              {blockedLeft
                ? t('signin.tryAgainIn', { time: formatSeconds(blockedLeft) })
                : t('signin.sendCode')}
            </Button>
            {consent}
          </div>
        </form>
      ) : (
        <div className="animate-rise flex flex-1 flex-col gap-6">
          <button
            type="button"
            onClick={changeIdentifier}
            className="-ms-2 inline-flex min-h-11 items-center gap-1 self-start rounded-md px-2 text-sm font-medium text-ink-2 hover:text-ink"
          >
            <ArrowBack size={18} />
            {t('common.back')}
          </button>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-balance">
              {t('signin.codeTitle')}
            </h1>
            <p className="mt-2 text-base text-ink-2">
              {busy === 'send' ? t('signin.sendingTo') : t('signin.codeSubtitle')}{' '}
              <span dir="ltr" className="tabular font-medium text-ink">
                {maskIdentifier(method, destination)}
              </span>
            </p>
          </div>

          <OtpInput
            ref={otpRef}
            label={t('signin.code')}
            value={code}
            onChange={(c) => {
              setCode(c);
              if (codeInvalid) setCodeInvalid(false);
            }}
            onComplete={(c) => void verify(c)}
            disabled={busy === 'send' || busy === 'verify'}
            invalid={codeInvalid}
          />

          {debugCode ? (
            <p className="rounded-md bg-attention-soft px-3 py-2 text-sm text-attention-text">
              {t('signin.devCode')}{' '}
              <code dir="ltr" className="tabular font-semibold">
                {debugCode}
              </code>
            </p>
          ) : null}

          <div aria-live="assertive" className="min-h-6">
            {busy === 'send' ? (
              <p className="flex items-center gap-3 text-sm text-ink-2" role="status">
                <LiveMark state="thinking" size={16} />
                <span>{t('signin.sending')}</span>
              </p>
            ) : busy === 'verify' ? (
              <p className="flex items-center gap-3" role="status">
                <LiveMark state="thinking" size={16} />
                <span className="sr-only">{t('signin.verifying')}</span>
              </p>
            ) : errorMessage ? (
              <p role="alert" className="flex items-start gap-2 text-sm text-critical-text">
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <span>
                  {errorMessage}
                  {method === 'phone' && error?.key === 'delivery_failed' ? (
                    <>
                      {' '}
                      <button type="button" onClick={useEmail} className="font-medium underline">
                        {t('signin.useEmail')}
                      </button>
                    </>
                  ) : null}
                </span>
              </p>
            ) : smsFailed && method === 'phone' ? (
              <p role="alert" className="flex items-start gap-2 text-sm text-critical-text">
                <AlertCircle size={18} className="mt-0.5 shrink-0" />
                <span>
                  {t('signin.smsFailed')}{' '}
                  <button type="button" onClick={useEmail} className="font-medium underline">
                    {t('signin.useEmail')}
                  </button>
                </span>
              </p>
            ) : null}
          </div>

          <div className="mt-auto flex flex-col gap-3 lg:mt-2">
            <Button
              type="button"
              variant="secondary"
              size="lg"
              block
              loading={busy === 'resend'}
              disabled={resendLeft > 0 || busy !== null || Boolean(blockedLeft)}
              onClick={() => void send('resend')}
            >
              {resendLeft > 0 ? (
                <span className="tabular">{t('signin.resendIn', { seconds: resendLeft })}</span>
              ) : (
                t('signin.resend')
              )}
            </Button>
            <div className="flex items-center justify-center gap-4">
              <button
                type="button"
                onClick={changeIdentifier}
                className="min-h-11 text-sm font-medium text-brand hover:underline"
              >
                {method === 'phone' ? t('signin.changeNumber') : t('signin.changeEmail')}
              </button>
              {method === 'phone' ? (
                <button
                  type="button"
                  onClick={useEmail}
                  className="min-h-11 text-sm font-medium text-brand hover:underline"
                >
                  {t('signin.useEmail')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </AuthLayout>
  );
}
